import type { ReactNode } from "react";
import { ArrowDown } from "lucide-react";
import { HideForSubscribers } from "./HideForSubscribers";

/**
 * The boundary between a free tool and the reference document under it, plus
 * the two signposts that make that document navigable.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROBLEM THIS SOLVES, MEASURED
 *
 * Every `/free/**` tool page is a live interface followed by 200–1,800 words of
 * prose, and until this file existed the only thing separating the two was an
 * HTML comment and a 3rem margin (`mt-12` / `mt-14`). Nothing told a reader the
 * interactive part had ended, so the essay read as more tool — which is most of
 * what "why is there so much text" is actually reacting to. The words were
 * never the whole complaint; the missing edge was.
 *
 * So: a hairline across the content column, an eyebrow that names what is below
 * it ("Reference"), and a table of contents that turns an unknown depth of
 * scrolling into a seven-line index. None of that deletes a sentence, which
 * matters because the owner's instruction about the explanations was "keep them
 * for now".
 *
 * WHY THE READING COLUMN IS CLAMPED HERE AND NOT ON EACH PAGE
 *
 * Three pages hand-wrote `max-w-3xl` on their `<article>` and a fourth
 * (`/free/server-cfg`) forgot, which was a live defect rather than a style
 * difference: that page's shell width is decided by the tool, so dropping a
 * `resources/` folder reflowed 1,765 words of body copy from a 48rem column to
 * an 80rem one, mid-session. Clamping here fixes it by construction — the
 * reading measure stops being a per-page decision, so it cannot be forgotten on
 * a seventh page and cannot be overridden by whatever the tool above it does
 * with the shell. `FreeToolShell` makes exactly this argument for the header,
 * the nav grid and the footer; reading measure is the same kind of decision.
 *
 * The clamp is `mx-auto`, so under a wide tool the prose is centred rather than
 * hugging the left edge of an 80rem `main` — which is what the three
 * hand-clamped articles did.
 *
 * WHY THE EYEBROW SITS INSIDE THE CLAMP AND THE HAIRLINE DOES NOT
 *
 * The hairline is the edge of the *tool*, so it spans the tool's column. The
 * eyebrow is the label of the *reading zone*, so it aligns with the first line
 * of prose it labels. On an 80rem page the alternative puts the word
 * "Reference" sixteen rem to the left of the paragraph it introduces, which
 * reads as a stray heading rather than a boundary marker.
 *
 * LIMITS
 *
 * No `<h2>` is emitted at the boundary and no `<h1>` anywhere, so the heading
 * outline of every page is exactly what it was — the eyebrow is a `<p>` on
 * purpose. This component also does not know what is inside it: the anchors in
 * `ArticleToc` are supplied by the page, written literally in its source rather
 * than slugified from heading text at runtime, so rewording a heading cannot
 * silently break a link that points at it. The cost of that choice is that the
 * two can drift; the compensation is that the drift is visible in the diff of
 * one file.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The id the jump row and the boundary agree on. One constant, two readers. */
export const READING_ZONE_ID = "reference";

/** Shared eyebrow treatment: the same one `FreeToolsNav` uses for "More free tools". */
const EYEBROW = "text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground";

/**
 * The tool/reading boundary and the clamped reading column.
 *
 * Rendered by `FreeToolShell` for every page that passes a `reading` node. It
 * is exported because `/free/server-cfg` cannot use that slot — its client
 * island owns the shell, so its `<article>` arrives as the island's children —
 * and the one thing worse than a boundary in two places is a boundary drawn two
 * different ways.
 */
export function ReadingZone({ children }: { children: ReactNode }) {
  // The article and FAQ are for search visitors deciding whether to use the
  // tool. A subscriber is just using it, so they get the tool alone; the
  // prerendered HTML still carries the full article for crawlers.
  return (
    <HideForSubscribers>
      <div
        id={READING_ZONE_ID}
        className="mt-12 scroll-mt-16 border-t border-border/50 pt-8 sm:mt-14"
      >
        <div className="mx-auto max-w-3xl">
          <p className={EYEBROW}>Reference</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </HideForSubscribers>
  );
}

/** One article section: the `id` written on its `<h2>`, and the words to link. */
export type TocEntry = { id: string; label: string };

/**
 * The article's sections as a compact index.
 *
 * ~40–70 server-rendered words that replace "an unknown amount of prose is
 * below this" with "here are the seven things it covers, click one". It is the
 * cheapest available answer to "too vertical" because it removes nothing, and
 * it is what the header's jump row points at.
 *
 * `<nav>` with a list, not a row of bare links: a screen reader user gets the
 * count before the contents, which is the same information the sighted reader
 * gets from the shape of the block.
 */
export function ArticleToc({ items }: { items: readonly TocEntry[] }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="On this page" className="rounded-xl border border-border/60 bg-card p-4">
      <p className={EYEBROW}>On this page</p>
      <ol className="mt-3 grid gap-1.5 text-sm leading-6 sm:grid-cols-2">
        {items.map((it) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              className="text-muted-foreground underline decoration-border underline-offset-4 transition-colors hover:text-foreground hover:decoration-primary/50"
            >
              {it.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Two links under the `<h1>`: down to the reference zone, and straight to the
 * FAQ.
 *
 * These exist because the tool comes first on every one of these pages and
 * should — a visitor arriving from "fivem duplicate spawn code" came to do
 * something, not to read — but that leaves a reader who arrived with a
 * *question* scrolling past an interface to find the answer. Two anchors, about
 * six words, and the page-length scroll is gone.
 *
 * They are plain anchors to ids that are in the prerendered HTML, so they work
 * before hydration and on a page with JavaScript disabled. The slot for a third
 * item ("Show me how", starting a walkthrough) is deliberately left empty here:
 * no free tool has a walkthrough yet and a jump row item that starts nothing is
 * worse than no item.
 */
export function ToolJumpRow() {
  // Jumps into the reading zone, which a subscriber never sees — hide with it.
  return (
    <HideForSubscribers>
      <p className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <a
          href={`#${READING_ZONE_ID}`}
          className="inline-flex items-center gap-1.5 font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          How it works
          <ArrowDown className="h-3.5 w-3.5" />
        </a>
        <a
          href="#faq"
          className="inline-flex items-center gap-1.5 font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          FAQ
          <ArrowDown className="h-3.5 w-3.5" />
        </a>
      </p>
    </HideForSubscribers>
  );
}
